import './styles/Modal.scss';

import { X } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import { useId } from 'react';
import { MouseEvent, PropsWithChildren, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { parseToKebabCase } from '../../helpers';
import { useClassHelper, useLockBodyScroll } from '../../hooks';
import { Button } from '../Button';

const className = 'c-Modal';

interface BaseProps {
  show: boolean;
  title: string;
  actionButtonLabel?: string;
  cancelButtonLabel?: string;
  loading: boolean;
  onClose: () => void;
  actionButton: () => void;
  actionButtonStyles?: string;
  actionButtonDisabled?: boolean;
  showBtnClose: boolean;
  size: 'small' | 'medium' | 'large' | 'default';
  /** 'sectioned' renders the close icon inside a padded header with a bottom border; body gets its own padding. */
  variant: 'default' | 'sectioned';
  /** Accessible name for title-less modals — applied as aria-label when no title is rendered. */
  ariaLabel?: string;
  verticalCentered: boolean;
  withPadding: boolean;
  classNames?: {
    container?: string;
    content?: string;
    header?: string;
  };
}

interface Props extends Partial<BaseProps>, PropsWithChildren {}

export const Modal = ({
  show = false,
  onClose,
  children,
  title,
  actionButton,
  actionButtonDisabled,
  actionButtonLabel,
  cancelButtonLabel,
  actionButtonStyles = undefined,
  loading = false,
  size = 'medium',
  showBtnClose = true,
  verticalCentered = true,
  withPadding = true,
  variant = 'default',
  ariaLabel,
  classNames = {},
}: Props) => {
  useLockBodyScroll(show);

  const c = useClassHelper(className);

  const randomId = useId(); // Number between 1 and 10
  const portalContainer = document.getElementById('modal-root');

  const titleId = `${title ? parseToKebabCase(title) : randomId}-title`;
  const descriptionId = `${title ? parseToKebabCase(title) : randomId}-desc`;

  const handleClose = (e: unknown) => {
    const event = e as MouseEvent;

    event.stopPropagation();

    if (loading) {
      return;
    }

    onClose();
  };

  useEffect(() => {
    if (show) {
      const keyPress = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleClose(e);
        } else if (actionButton && e.key === 'Enter') {
          actionButton();
        }
      };

      window.addEventListener('keydown', keyPress);

      return () => window.removeEventListener('keydown', keyPress);
    }
  }, [show]);

  const isOnClose = Boolean(onClose);
  const isActionsBtns = Boolean(actionButton);
  const isSectioned = variant === 'sectioned';

  const closeButton = (
    <button
      type="button"
      className={c('__icon-close', {
        '__icon-close--inline': isSectioned,
      })}
      onClick={handleClose}
      aria-label="close modal button"
      aria-disabled={loading}
      tabIndex={0}
    >
      <X size={24} aria-label="close modal icon" />
    </button>
  );

  const renderFooter = () => {
    if ((isOnClose && showBtnClose) || isActionsBtns) {
      return (
        <footer className={c('__footer')}>
          <>
            {isOnClose && showBtnClose && (
              <Button
                type="button"
                color="tertiary"
                classes={{ button: c('__button-cancel') }}
                onClick={handleClose}
                disabled={loading}
              >
                {cancelButtonLabel ?? 'Cancel'}
              </Button>
            )}

            {isActionsBtns && (
              <Button
                type="button"
                classes={{ content: c('__button-start') }}
                onClick={actionButton}
                disabled={actionButtonDisabled}
                className={actionButtonStyles}
                loading={loading}
              >
                {actionButtonLabel ?? 'Discard'}
              </Button>
            )}
          </>
        </footer>
      );
    }
  };

  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.div
          className={`${className} ${className}--${
            verticalCentered ? 'centered' : ''
          } ${classNames.container || ''}`}
          onClick={handleClose}
          role="alertdialog"
          aria-labelledby={title ? titleId : undefined}
          aria-label={!title ? ariaLabel : undefined}
          aria-describedby={descriptionId}
          tabIndex={0}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className={`${c('__content', `--${size}`, {
              '__content--with-padding': withPadding && !isSectioned,
            })} ${classNames.content || ''} `}
            onClick={event => event.stopPropagation()}
            initial={{ y: 25 }}
            animate={{ y: 0 }}
            exit={{ y: 25 }}
          >
            {title && (
              <header
                className={`${c('__header', {
                  '__header--sectioned': isSectioned,
                })} ${classNames.header || ''}`}
              >
                <h2 className={c('__title')} id={titleId}>
                  {title}
                </h2>
                {isSectioned && closeButton}
              </header>
            )}

            {!isSectioned && closeButton}

            <div
              id={descriptionId}
              className={isSectioned ? c('__body--sectioned') : undefined}
            >
              {children}
            </div>

            {renderFooter()}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    portalContainer,
  );
};
